import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  CloudArrowUp, PencilSimple, PaperPlaneTilt, ThumbsUp, ThumbsDown,
  CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import { getEvvStatusBadge, fmtDate, fmtTime } from './helpers';

const ROW_BG = { failed: 'bg-red-50/40', pending_approval: 'bg-amber-50/20' };

export const EVVResolutionTable = ({
  entries, loading, total, page, totalPages,
  selectedIds, onToggleSelect, onToggleSelectAll,
  onEditEntry, onRequestApproval, onOpenApproval,
  onSubmitSingle, onPageChange, canApprove,
}) => {
  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="text-center py-16">
            <CloudArrowUp size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No EVV entries found</p>
            <p className="text-sm text-slate-400 mt-1">Create visits in the EVV page or adjust your filters</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selectedIds.length === entries.length && entries.length > 0}
                    onCheckedChange={onToggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Approval</TableHead>
                <TableHead>Error</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => {
                const status = entry.sandata_status || 'draft';
                return (
                  <TableRow
                    key={entry.id}
                    className={ROW_BG[status] || ''}
                    data-testid={`entry-row-${entry.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(entry.id)}
                        onCheckedChange={() => onToggleSelect(entry.id)}
                      />
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(entry.service_date)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{entry.patient_name}</p>
                      {entry.patient_medicaid_id && (
                        <p className="text-xs text-slate-400 font-mono">{entry.patient_medicaid_id}</p>
                      )}
                    </TableCell>
                    <TableCell><p className="text-sm">{entry.employee_name}</p></TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {fmtTime(entry.time_in)} - {fmtTime(entry.time_out)}
                    </TableCell>
                    <TableCell><span className="text-xs font-mono">{entry.service_code}</span></TableCell>
                    <TableCell>{getEvvStatusBadge(status)}</TableCell>
                    <TableCell>
                      {entry.approved_by && status === 'approved' && (
                        <p className="text-xs text-teal-600" data-testid={`approved-by-${entry.id}`}>
                          By {entry.approved_by}
                        </p>
                      )}
                      {entry.rejected_by && status === 'rejected_approval' && (
                        <div>
                          <p className="text-xs text-rose-600">By {entry.rejected_by}</p>
                          {entry.rejection_reason && (
                            <p className="text-xs text-rose-500 truncate max-w-[150px]" title={entry.rejection_reason}>
                              {entry.rejection_reason}
                            </p>
                          )}
                        </div>
                      )}
                      {entry.approval_requested_by && status === 'pending_approval' && (
                        <p className="text-xs text-amber-600">Req. by {entry.approval_requested_by}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.last_error_code && (
                        <div className="max-w-[180px]">
                          <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">{entry.last_error_code}</Badge>
                          <p className="text-xs text-red-600 mt-1 truncate" title={entry.last_error_message}>
                            {entry.last_error_message}
                          </p>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onEditEntry(entry)} title="Edit / Correct" data-testid={`edit-entry-${entry.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        {['draft', 'corrected', 'failed', 'rejected_approval'].includes(status) && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => onRequestApproval([entry.id])}
                            title="Request Approval"
                            data-testid={`request-approval-${entry.id}`}
                          >
                            <PaperPlaneTilt size={16} className="text-amber-600" />
                          </Button>
                        )}
                        {canApprove && status === 'pending_approval' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => onOpenApproval('approve', entry.id)} title="Approve" data-testid={`approve-entry-${entry.id}`}>
                              <ThumbsUp size={16} className="text-teal-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => onOpenApproval('reject', entry.id)} title="Reject" data-testid={`reject-entry-${entry.id}`}>
                              <ThumbsDown size={16} className="text-rose-600" />
                            </Button>
                          </>
                        )}
                        {status === 'approved' && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => onSubmitSingle(entry.id)}
                            title="Submit to Sandata"
                            data-testid={`submit-entry-${entry.id}`}
                          >
                            <CloudArrowUp size={16} className="text-indigo-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t">
          <p className="text-sm text-slate-500">Showing {entries.length} of {total} entries</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} data-testid="prev-page">
              <CaretLeft size={14} />
            </Button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} data-testid="next-page">
              <CaretRight size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
